import { type ReactNode } from 'react';
import { Box } from '@mui/material';
import { DetailCard } from '../DetailCard';
import { FieldGrid } from '../FieldGrid';
import { resolveSection } from '../../../lib/resolveFields';
import type { SectionDef, SectionVisual } from './types';

// ── Internal grouping ─────────────────────────────────────────────────────────

type SectionGroup<T extends Record<string, unknown>, K extends string> =
  | { paired: false; section: SectionDef<T, K> }
  | { paired: true; sections: SectionDef<T, K>[] };

function groupSections<T extends Record<string, unknown>, K extends string>(
  sections: SectionDef<T, K>[],
): SectionGroup<T, K>[] {
  const result: SectionGroup<T, K>[] = [];
  const seen = new Set<string>();
  for (const section of sections) {
    if (!section.pairGroup) {
      result.push({ paired: false, section });
    } else if (!seen.has(section.pairGroup)) {
      const siblings = sections.filter(s => s.pairGroup === section.pairGroup);
      result.push({ paired: true, sections: siblings });
      siblings.forEach(s => seen.add(s.pairGroup!));
    }
  }
  return result;
}

// ── SectionCard ───────────────────────────────────────────────────────────────

export type SectionCardProps<T extends Record<string, unknown>> = {
  section: SectionDef<T>;
  data: T;
  t: (key: string) => string;
  visual: SectionVisual;
  icon: ReactNode;
  stretch?: boolean;
  /** Render the card body foldable behind a header chevron toggle. */
  collapsible?: boolean;
};

export function SectionCard<T extends Record<string, unknown>>({
  section,
  data,
  t,
  visual,
  icon,
  stretch,
  collapsible,
}: SectionCardProps<T>) {
  if (section.showWhenKey && !data[section.showWhenKey as string]) return null;

  const allFields = resolveSection(section.fields, data, t);
  // A field carrying a custom `node` (e.g. a status badge) counts as content, even
  // when its resolved text `value` is null — otherwise hideEmptyFields/hideWhenEmpty
  // would silently drop a rendered field.
  const isEmptyField = (f: (typeof allFields)[number]) => f.value === null && f.node === undefined;
  const fields = section.hideEmptyFields ? allFields.filter(f => !isEmptyField(f)) : allFields;

  if (section.hideWhenEmpty && fields.every(isEmptyField)) return null;

  return (
    <DetailCard
      icon={icon}
      iconBg={visual.iconBg}
      title={t(section.titleKey)}
      stretch={stretch}
      collapsible={collapsible && (section.collapsible ?? true)}
      defaultExpanded={section.defaultExpanded ?? false}
    >
      <FieldGrid fields={fields} stack={section.stack} />
    </DetailCard>
  );
}

// ── ColumnSections ────────────────────────────────────────────────────────────

export type ColumnSectionsProps<T extends Record<string, unknown>, K extends string = string> = {
  sections: SectionDef<T, K>[];
  data: T;
  t: (key: string) => string;
  /**
   * Keyed by section id. Partial so TypeScript surfaces the undefined case —
   * a missing entry skips the section with a warning instead of throwing.
   */
  visualMap: Partial<Record<K, SectionVisual>>;
  iconMap: Partial<Record<K, (color: string) => ReactNode>>;
  /** Wraps the last rendered card in a flex:1 container so it fills remaining column height. */
  stretchLast?: boolean;
  /**
   * Render every card foldable behind a header chevron toggle. Each section's
   * initial state comes from its own `defaultExpanded` (collapsed by default).
   */
  collapsible?: boolean;
};

export function ColumnSections<T extends Record<string, unknown>, K extends string = string>({
  sections,
  data,
  t,
  visualMap,
  iconMap,
  stretchLast,
  collapsible,
}: ColumnSectionsProps<T, K>) {
  const groups = groupSections(sections);

  function renderSection(s: SectionDef<T, K>, stretch?: boolean) {
    const visual = visualMap[s.id];
    const iconFn = iconMap[s.id];
    if (!visual || !iconFn) {
      console.warn(`[ColumnSections] missing token for section id "${s.id}"`);
      return null;
    }
    return (
      // key set here so it is present whether the card is returned directly into an array or wrapped in a Box
      <SectionCard
        key={s.id}
        section={s}
        data={data}
        t={t}
        visual={visual}
        icon={iconFn(visual.iconColor)}
        stretch={stretch}
        collapsible={collapsible}
      />
    );
  }

  return (
    <>
      {groups.map((group, idx) => {
        const isLast = stretchLast && idx === groups.length - 1;

        if (!group.paired) {
          const card = renderSection(group.section, isLast);
          if (!isLast) return card;
          return (
            <Box key={group.section.id} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {card}
            </Box>
          );
        }

        return (
          <Box
            key={group.sections[0].pairGroup}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {group.sections.map(s => renderSection(s))}
          </Box>
        );
      })}
    </>
  );
}
